import { CommonModule } from '@angular/common';
import { Component, OnInit, HostListener, Optional, OnDestroy } from '@angular/core';
import { RouterModule } from '@angular/router';
import { ReactiveFormsModule } from '@angular/forms';
import { GlobalService } from '../../services/global.service';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { environment } from 'src/environments/environment';
import { filter, Subject, takeUntil } from 'rxjs';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { SessionService } from '../../services/session.service';
import { IdleService } from '../../services/idle.Service';
import { TranslateModule } from '@ngx-translate/core';
import { AdminFeedbackService } from '../../services/admin-feedback.service';

@Component({
  selector: 'app-activeadmin',
  templateUrl: './activeadmin.component.html',
  styleUrls: ['./activeadmin.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatTooltipModule,
    MatMenuModule,
    MatIconModule,
    MatDividerModule,
    MatDialogModule,
    RouterModule,
    TranslateModule,
  ],
})
export class ActiveadminComponent implements OnInit, OnDestroy {
  appVersionTooltip = '';
  buildTimestamp = '';

  showSimpleDebug = false;
  showGlobalStateDialog = false;
  isMaximized = false;

  // Screen info
  screenInfo = '';
  screenWidth = 0;
  screenHeight = 0;

  // Console logging control
  consoleEnabled = true;

  private destroy$ = new Subject<void>(); // Adicionado

  pendingFeedbackCount = 0;

  constructor(
    public globalService: GlobalService,
    @Optional() private swUpdate: SwUpdate,
    private sessionService: SessionService,
    private idleService: IdleService,
    private dialog: MatDialog,
    private feedbackService: AdminFeedbackService,
  ) {}

  async openHomepageLayoutManagement(): Promise<void> {
    const { HomepagelayoutmanagementComponent } =
      await import('src/app/pages/home/homepagelayoutmanagement/homepagelayoutmanagement.component');
    const dialogRef = this.dialog.open(HomepagelayoutmanagementComponent, {
      width: '95%',
      maxWidth: '1200px',
      disableClose: true,
      autoFocus: false,
      panelClass: 'homepage-layout-dialog',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(saved => {
        if (saved) {
          window.location.reload();
        }
      });
  }

  openProductsQuickManagement(): void {
    import('../dialogs/products-quick-management-dialog/products-quick-management-dialog.component').then(module => {
      const dialogConfig = this.globalService.isMobileDevice
        ? { maxWidth: '100vw', height: '100%', width: '100%' }
        : { maxWidth: '1100px', width: '90vw' };

      this.dialog.open(module.ProductsQuickManagementDialogComponent, {
        ...dialogConfig,
        autoFocus: false,
        disableClose: false,
      });
    });
  }

  ngOnDestroy(): void {
    if (!this.destroy$.closed) {
      this.destroy$.next();

      this.destroy$.complete();
    }
  }

  ngOnInit(): void {
    // Build timestamp (se disponível no environment)
    this.buildTimestamp = (environment as any).buildTimestamp || new Date().toISOString();

    // Versão do build
    const client = environment.appVersion || '';
    this.updateVersionTooltip(client);

    // Service Worker version tracking
    if (this.swUpdate?.isEnabled) {
      this.swUpdate.versionUpdates
        .pipe(
          filter((e): e is VersionReadyEvent => e.type === 'VERSION_READY'),
          takeUntil(this.destroy$),
        )
        .subscribe(e => {
          const current = (e.currentVersion.appData as any)?.version;
          const latest = (e.latestVersion.appData as any)?.version;

          if (latest && latest !== client) {
            this.updateVersionTooltip(client, latest);
          } else if (current && current !== client) {
            this.updateVersionTooltip(client, current);
          }
        });
    }

    // Initialize screen info
    this.updateScreenInfo();

    // Carregar contadores de tarefas pendentes
    this.loadPendingFeedbackCount();
    this.feedbackService.ensureRealtimeListening();
  }

  @HostListener('window:resize')
  onResize() {
    this.updateScreenInfo();
  }

  private updateVersionTooltip(client: string, sw?: string): void {
    const env = environment.production ? 'PROD' : 'DEV';
    const buildDate = new Date(this.buildTimestamp).toLocaleString('pt-PT');

    let tooltip = `UI v${client} | ${env}`;
    if (sw) tooltip += ` | SW v${sw}`;
    tooltip += `\nBuild: ${buildDate}`;

    this.appVersionTooltip = tooltip;
  }

  private updateScreenInfo(): void {
    this.screenWidth = window.innerWidth;
    this.screenHeight = window.innerHeight;

    let breakpoint = 'XS';
    if (this.screenWidth >= 1920) breakpoint = 'XL';
    else if (this.screenWidth >= 1280) breakpoint = 'LG';
    else if (this.screenWidth >= 960) breakpoint = 'MD';
    else if (this.screenWidth >= 600) breakpoint = 'SM';

    this.screenInfo = `${this.screenWidth}x${this.screenHeight} (${breakpoint})`;
  }

  get environmentName(): string {
    return environment.production ? 'PROD' : 'DEV';
  }

  get environmentColor(): string {
    return environment.production ? '#dc3545' : '#28a745';
  }

  // Clear browser cache and reload
  clearCacheAndReload(): void {
    if (confirm('Limpar cache e recarregar a aplicação?')) {
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => caches.delete(name));
        });
      }
      localStorage.clear();
      sessionStorage.clear();
      window.location.reload();
    }
  }

  // Toggle console logging
  toggleConsoleLogging(): void {
    this.consoleEnabled = !this.consoleEnabled;

    if (!this.consoleEnabled) {
      // Disable console
      (window as any).console = {
        log: () => {},
        warn: () => {},
        error: () => {},
        info: () => {},
        debug: () => {},
      };
    } else {
      // Re-enable (reload required for full functionality)
      window.location.reload();
    }
  }

  // Open state inspector dialog
  openStateInspector(): void {
    const stateData = {
      user: this.globalService.loggedUserClass,
      activeRequests: this.globalService.activeRequests,
      environment: {
        production: environment.production,
        version: environment.appVersion,
        buildTimestamp: this.buildTimestamp,
      },
      screen: {
        width: this.screenWidth,
        height: this.screenHeight,
        devicePixelRatio: window.devicePixelRatio,
        userAgent: navigator.userAgent,
      },
      localStorage: { ...localStorage },
      sessionStorage: { ...sessionStorage },
    };

    // Copy to clipboard
    navigator.clipboard.writeText(JSON.stringify(stateData, null, 2)).then(() => {
      alert(
        'Estado copiado para clipboard!\n\nVersão: ' + environment.appVersion + '\nAmbiente: ' + this.environmentName,
      );
    });
  }

  // Copy current route to clipboard
  copyCurrentRoute(): void {
    const route = window.location.href;
    navigator.clipboard.writeText(route).then(() => {
      console.log('Route copiado:', route);
    });
  }

  // --- FERRAMENTAS DE TESTE DE ALERTAS ---
  testSessionWarning(): void {
    if (typeof (this.sessionService as any).debugTriggerWarning === 'function') {
      (this.sessionService as any).debugTriggerWarning();
    } else {
      console.warn('SessionService.debugTriggerWarning não encontrado. Recarregue a página.');
    }
  }

  testInactivityWarning(): void {
    if (typeof (this.idleService as any).showWarning === 'function') {
      (this.idleService as any).showWarning();
    } else {
      console.warn('IdleService.showWarning não encontrado. Recarregue a página.');
    }
  }

  // --- FEEDBACK IN-APP ---
  loadPendingFeedbackCount(): void {
    if (this.globalService.loggedUserClass?.Admin) {
      this.feedbackService
        .getSummary()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: any) => {
            const data = res?.data || res || {};
            const pendente = Number(data.Pendente) || 0;
            const analise = Number(data.EmAnalise || data['Em Análise']) || 0;
            const dev = Number(data.EmDesenvolvimento || data['Em Desenvolvimento']) || 0;
            const pedirInfo = Number(data.PedirInformacao || data['Pedir Informação']) || 0;
            this.pendingFeedbackCount = pendente + analise + dev + pedirInfo;
          },
          error: () => {},
        });
    }
  }

  openFeedbackDialog(): void {
    import('../admin-feedback/admin-feedback-dialog/admin-feedback-dialog.component').then(m => {
      const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
      const dialogRef = this.dialog.open(m.AdminFeedbackDialogComponent, {
        width: isMobile ? '100vw' : '96vw',
        maxWidth: isMobile ? '100vw' : '96vw',
        height: isMobile ? '100%' : '92vh',
        maxHeight: isMobile ? '100%' : '94vh',
        position: isMobile ? { top: '0px', left: '0px' } : undefined,
        autoFocus: false,
        panelClass: 'admin-feedback-dialog-panel',
      });

      dialogRef
        .afterClosed()
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.loadPendingFeedbackCount();
        });
    });
  }
}
